<template>
  <div id="dashboard" class="container">
    <!-- Initial load: the meeting snapshot is async and votes don't fetch
         until the meetingId resolves. Show a spinner while any of that is
         still in flight so the dashboard never flashes an empty "not voted"
         state on first entry. -->
    <div v-if="isLoading" class="dashboard-loading">
      <ProgressSpinner style="width: 50px; height: 50px;" stroke-width="3" />
      <p>Loading…</p>
    </div>

    <!-- No current meeting configured -->
    <div v-else-if="!meetingStore.currentMeeting" class="no-meeting">
      <h3 class="title is-4">No active meeting</h3>
      <p>
        There's no meeting marked as current right now. Please check back
        later or contact an administrator.
      </p>
    </div>

    <div v-else-if="!meetingStore.isVotingClosed">
      <!-- Voted -->
      <div v-if="userStore.votes.length > 0" class="columns">
        <div class="column is-full">
          <div class="columns">
            <div class="column is-half">
              <MeetingDetails />
              <br />
              <MeetingLink />
              <br />
              <div v-if="userVotes.length > 0">
                <h3 class="title is-5 has-text-centered" style="color: green;">
                  You have voted.
                </h3>
                <VoteSummary
                  v-for="v in userVotes"
                  :key="v.id"
                  :vote-record="v"
                  :display-name="userStore.profile.name"
                  :display-shares="userStore.profile.shares"
                />
              </div>

              <div v-if="custodianVotes.length > 0">
                <br />
                <h3 class="title is-5 has-text-centered" style="color: green;">
                  Custodial Votes
                </h3>
                <VoteSummary
                  v-for="v in custodianVotes"
                  :key="v.id"
                  :vote-record="v"
                  :display-name="`${userStore.profile.name} for ${v.vote.custodian}`"
                  :display-shares="v.vote.totalshares"
                />
              </div>
            </div>
            <div class="column is-half">
              <h5 class="title is-5">Corrections:</h5>
              <p>
                You have until {{ meetingStore.closedDate }} at
                {{ meetingStore.closedTime }} to make changes to your proxy.
              </p>
              <br />
              <router-link
                v-if="meetingStore.meetingId"
                :to="`/vote/${meetingStore.meetingId}`"
              >
                <Button label="New Online Vote" severity="warn" size="large" />
              </router-link>
              <br /><br />
              <router-link
                v-if="meetingStore.meetingId"
                :to="`/upload/${meetingStore.meetingId}`"
              >
                <Button label="Upload New Proxy" severity="warn" size="large" />
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Not voted -->
      <div v-else>
        <div class="columns">
          <div class="column is-half">
            <router-link
              v-if="meetingStore.meetingId"
              :to="`/vote/${meetingStore.meetingId}`"
              class="main_button"
            >
              Vote Online
            </router-link>
          </div>
          <div class="column is-half">
            <router-link
              v-if="meetingStore.meetingId"
              :to="`/upload/${meetingStore.meetingId}`"
              class="main_button"
            >
              Upload Proxy
            </router-link>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <MeetingDetails />
            <MeetingLink />
          </div>
        </div>
      </div>
    </div>

    <!-- Voting closed -->
    <div v-else>
      <h3 class="title is-3">
        Voting has closed as of {{ meetingStore.closedDate }},
        {{ meetingStore.closedTime }}.
      </h3>
      <MeetingDetails />
      <MeetingLink />
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'
import MeetingDetails from '@/components/MeetingDetails.vue'
import MeetingLink from '@/components/MeetingLink.vue'
import VoteSummary from '@/components/VoteSummary.vue'
import { useUserStore } from '@/stores/user'
import { useMeetingStore } from '@/stores/meeting'

const userStore = useUserStore()
const meetingStore = useMeetingStore()

// True while either:
// - the meeting subscription hasn't delivered its first snapshot yet, or
// - we know a meeting but haven't finished the first votes fetch for it.
// Without this gate the dashboard would briefly render the "not voted"
// branch on first entry because currentMeeting is null and votes is [].
// Once votes have loaded once, subsequent re-fetches don't re-show the
// spinner — we just update in-place.
const isLoading = computed(() => {
  if (meetingStore.loading) return true
  if (!meetingStore.currentMeeting) return false
  return !userStore.votesLoaded
})

// Only the most recent personal vote is effective — a new online vote or
// uploaded proxy supersedes any earlier submission. Older entries stay in
// Firestore for the admin audit view but aren't shown on the dashboard.
// `userStore.votes` is already ordered createdOn DESC.
const userVotes = computed(() => {
  const latest = userStore.votes.find((v) => !v?.vote?.signcustodian)
  return latest ? [latest] : []
})

// For custodial votes, keep the latest submission per custodian identity so
// a shareholder voting on behalf of multiple custodians still sees one row
// per custodian, always showing the newest entry for that custodian.
const custodianVotes = computed(() => {
  const byKey = new Map()
  for (const v of userStore.votes) {
    if (!v?.vote?.signcustodian) continue
    const key = v.vote.custodianshn || v.vote.custodian || v.id
    if (!byKey.has(key)) byKey.set(key, v)
  }
  return Array.from(byKey.values())
})

// The meeting store is populated asynchronously via onSnapshot, so the
// meetingId may not be known on first mount. Watch for it and (re)load
// the votes scoped to just the current meeting.
watch(
  () => meetingStore.meetingId,
  (id) => {
    userStore.getVotesForUser(id)
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
@use '@/theme.scss' as *;

#dashboard {
  margin-top: 50px;
  padding: 20px;

  .main_button {
    display: block;
    font-size: 30px;
    background-color: $primary;
    padding: 30px;
    border-radius: 15px;
    color: white;
    text-align: center;
    line-height: 40px;
    cursor: pointer;
  }

  .dashboard-loading,
  .no-meeting {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    gap: 12px;
    color: var(--bulma-text-weak, #777);
    text-align: center;
  }
}
</style>
